/**
 * LoginPage — minimal brand-themed login card.
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Card, CardContent, TextField, Typography, Alert } from '@mui/material';
import { useAuth } from '../auth/AuthProvider';
import { PORTAL_META } from '../registry/portalMeta';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login, loading, error } = useAuth();
  const expired = params.get('expired') === '1';
  // Same-origin path only: must start with '/', never '//' (open-redirect guard)
  const rawNext = params.get('next') ?? '/';
  const next = rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : '/';
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  // Optional tenant override — prefilled from the last session; most platforms
  // have a single tenant and never need to touch this.
  const [tenantId, setTenantId] = useState(() => localStorage.getItem('tenant_id') ?? '');
  const [showTenant, setShowTenant] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login({ username, password, tenant_id: tenantId.trim() || undefined });
      navigate(next);
    } catch {
      // error surfaced via useAuth().error
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <Card variant="outlined" sx={{ width: 380 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography
            variant="h5"
            fontWeight={700}
            color="primary"
            sx={{ mb: 3, textAlign: 'center' }}
          >
            {PORTAL_META.platformTitle}
          </Typography>
          {expired && !error && (
            <Alert severity="info" sx={{ mb: 2 }}>Your session expired. Sign in to continue.</Alert>
          )}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <form onSubmit={handleSubmit}>
            <TextField
              label="Username"
              fullWidth
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              sx={{ mb: 2 }}
              autoFocus
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 3 }}
            />
            {showTenant ? (
              <TextField
                label="Tenant ID (optional)"
                fullWidth
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                sx={{ mb: 3 }}
                size="small"
              />
            ) : (
              <Button size="small" onClick={() => setShowTenant(true)} sx={{ mb: 2, textTransform: 'none' }}>
                Sign in to a specific tenant…
              </Button>
            )}
            <Button type="submit" variant="contained" fullWidth disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
